import { styled } from "@mui/material";
import { Icon, MenuItem } from "@trimble/modus-cmx";
import { useRef, useState, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useAccount } from "@/context/account";
import { FeatureFlaggedComponent } from "@/context/FeatureFlags";
import { UiFeatureFlagNames } from "@/gql/schema-types";
import { ADD_DEVICE_NAVIGATION_STATE } from "@/components/Devices/addDeviceNavigationState";
import { useAddDeviceDialog } from "@/components/Devices/useAddDeviceDialog";
import ExportPriceListModal from "@/components/ProductPricelist/ExportPriceListModal";
import MenuHeaderWrapper from "@/components/ui/Menu/MenuHeaderWrapper";
import OverflowMenu, { type OverflowMenuRef } from "@/components/OverflowMenu";
import {
  PermissionAction,
  PermissionCategory,
  PermissionGate,
} from "@/permissions";

const QuickAccessMenuItem = styled(MenuItem)({
  ".modus-wc-menu-item button .modus-wc-menu-item-content .modus-wc-menu-item-labels":
    {
      paddingInlineStart: 0,
    },
});

type QuickAccessActionProps = {
  icon: ComponentProps<typeof Icon>["name"];
  label: string;
  onClick: () => void;
};

function QuickAccessAction(props: QuickAccessActionProps) {
  const { icon, label, onClick } = props;

  return (
    <QuickAccessMenuItem label={label} onItemSelect={onClick}>
      <Icon
        slot="start-icon"
        name={icon}
        size="sm"
        decorative
        style={{ color: "var(--modus-wc-color-trimble-blue)" }}
      />
    </QuickAccessMenuItem>
  );
}

function QuickAccess() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { accountRouteId } = useAccount();
  const addDeviceDialog = useAddDeviceDialog();
  const menuRef = useRef<OverflowMenuRef>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const handleClose = () => {
    if (menuRef.current) {
      menuRef.current.menuVisible = false;
    }
  };

  const handleCreateContract = () => {
    if (!accountRouteId) return;
    handleClose();
    navigate(`/${accountRouteId}/contracts/create`);
  };

  const handleCreateCustomer = () => {
    if (!accountRouteId) return;
    handleClose();
    navigate(`/${accountRouteId}/customers/create`);
  };

  const handleAddDevice = () => {
    if (!accountRouteId) return;
    handleClose();
    const devicesPath = `/${accountRouteId}/devices`;

    if (location.pathname.replace(/\/+$/, "") === devicesPath) {
      void addDeviceDialog.open();
      return;
    }

    navigate(devicesPath, { state: ADD_DEVICE_NAVIGATION_STATE });
  };

  return (
    <>
      <OverflowMenu
        ref={menuRef}
        buttonAriaLabel={t("global_settings.quick_access.title")}
        iconName="add_circle"
        iconStyle={{ color: "var(--modus-wc-color-neutral-content)" }}
        placement="bottom-end"
        offset={[0, 2]}
      >
        <MenuHeaderWrapper title={t("global_settings.quick_access.title")}>
          <PermissionGate
            resource={PermissionCategory.Contract}
            action={PermissionAction.CreateAndManage}
          >
            <QuickAccessAction
              icon="document"
              label={t("global_settings.quick_access.create_new_contract")}
              onClick={handleCreateContract}
            />
          </PermissionGate>
          <PermissionGate
            resource={PermissionCategory.Customer}
            action={PermissionAction.Create}
          >
            <QuickAccessAction
              icon="person_add"
              label={t("global_settings.quick_access.create_new_customer")}
              onClick={handleCreateCustomer}
            />
          </PermissionGate>
          <PermissionGate
            resource={PermissionCategory.Product}
            action={PermissionAction.Export}
          >
            <FeatureFlaggedComponent
              flagName={UiFeatureFlagNames.ExportPriceList}
            >
              <QuickAccessAction
                icon="file_type_csv"
                label={t("global_settings.quick_access.export_price_list")}
                onClick={() => {
                  handleClose();
                  setExportModalOpen(true);
                }}
              />
            </FeatureFlaggedComponent>
          </PermissionGate>
          <PermissionGate
            resource={PermissionCategory.Device}
            action={PermissionAction.View}
          >
            <FeatureFlaggedComponent flagName={UiFeatureFlagNames.AddDevice}>
              <QuickAccessAction
                icon="add"
                label={t("global_settings.quick_access.add_device")}
                onClick={handleAddDevice}
              />
            </FeatureFlaggedComponent>
          </PermissionGate>
        </MenuHeaderWrapper>
      </OverflowMenu>
      {exportModalOpen ? (
        <ExportPriceListModal
          open
          onClose={() => {
            setExportModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

export default QuickAccess;
